import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type {
  BirthPrincipals,
  DeathPrincipals,
  DiplomaticMission,
  MarriagePrincipals,
  OverseasCivilEvent,
  OverseasEventType,
  Page,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const EVENT_TYPES: OverseasEventType[] = ['BIRTH', 'DEATH', 'MARRIAGE'];
const NIN_PATTERN = /^\d{3}-\d{4}-\d-\d-\d{2}$/;

function principalHeadline(e: OverseasCivilEvent): string {
  if (e.eventType === 'BIRTH') {
    const p = e.principals as BirthPrincipals;
    return `${p.childGivenNames ?? '?'} ${p.childFamilyName ?? ''}`.trim() || 'Unnamed child';
  }
  if (e.eventType === 'DEATH') {
    const p = e.principals as DeathPrincipals;
    return `NIN ${p.deceasedNin ?? '?'}`;
  }
  const p = e.principals as MarriagePrincipals;
  return `${p.partyAName ?? '?'} & ${p.partyBName ?? '?'}`;
}

/**
 * Overseas civil events — births/deaths/marriages abroad reported through a
 * mission, dual-control attested by the head of mission, then forwarded to
 * M01's real births/deaths/marriages-intent endpoints (spec: "OverseasCivilEvent").
 */
export function OverseasCivilEvents() {
  const { persona } = useAuth();
  const isMissionHead = persona?.roles.includes('MISSION_HEAD') ?? false;
  const isStaff = (persona?.roles.includes('CONSULAR_OFFICER') || isMissionHead) ?? false;
  const [status, setStatus] = useState('');
  const [eventType, setEventType] = useState('');
  const [missionId, setMissionId] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const missionsQ = useApiQuery<Page<DiplomaticMission>>('/api/v1/m42/missions?limit=100&status=active');
  const missions = useMemo(() => missionsQ.data?.items ?? [], [missionsQ.data]);
  const missionByw = useMemo(() => new Map(missions.map((m) => [m.id, m])), [missions]);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (eventType) params.set('type', eventType);
    if (missionId) params.set('missionId', missionId);
    return `/api/v1/m42/overseas-civil-events?${params.toString()}`;
  }, [status, eventType, missionId]);

  const listQ = useApiQuery<Page<OverseasCivilEvent>>(path);

  return (
    <div>
      <PageHeader
        title="Overseas Civil Events"
        subtitle="Births, deaths & marriages abroad — mission attestation feeding M01's national civil registry."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Report event
          </button>
        }
      />

      {!isStaff ? <RoleNotice roles={['CONSULAR_OFFICER', 'MISSION_HEAD']} /> : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select className="field-input !w-auto" value={missionId} onChange={(e) => setMissionId(e.target.value)}>
          <option value="">All missions</option>
          {missions.map((m) => (
            <option key={m.id} value={m.id}>
              {m.code}
            </option>
          ))}
        </select>
        <select className="field-input !w-auto" value={eventType} onChange={(e) => setEventType(e.target.value)}>
          <option value="">All types</option>
          {EVENT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t.charAt(0) + t.slice(1).toLowerCase()}
            </option>
          ))}
        </select>
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All states</option>
          <option value="REPORTED">Reported</option>
          <option value="MISSION_ATTESTED">Mission attested</option>
          <option value="FORWARDED_TO_M01">Forwarded to M01</option>
          <option value="REJECTED">Rejected</option>
        </select>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No overseas civil events match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Type</th>
                <th className="table-head">Principal(s)</th>
                <th className="table-head">Mission</th>
                <th className="table-head">Event date</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((e) => (
                <tr key={e.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(e.id)}>
                  <td className="table-cell text-xs font-semibold">{e.eventType}</td>
                  <td className="table-cell font-medium">{principalHeadline(e)}</td>
                  <td className="table-cell font-mono text-xs">{missionByw.get(e.missionId)?.code ?? e.missionId}</td>
                  <td className="table-cell">{formatDate(e.eventDate)}</td>
                  <td className="table-cell">
                    <StatusBadge status={e.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <EventDetailModal
          id={selectedId}
          missionByw={missionByw}
          isStaff={isStaff}
          isMissionHead={isMissionHead}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}

      {showCreate ? (
        <CreateEventModal
          missions={missions}
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function PrincipalDetails({ e }: { e: OverseasCivilEvent }) {
  if (e.eventType === 'BIRTH') {
    const p = e.principals as BirthPrincipals;
    return (
      <>
        <Detail label="Child" value={`${p.childGivenNames} ${p.childFamilyName}`} />
        <Detail label="Sex" value={p.childSex} />
        <Detail label="Place of birth" value={p.childPlaceOfBirth} />
        <Detail label="Mother" value={`${p.motherName} (${p.motherNin})`} mono />
        {p.fatherNin ? <Detail label="Father" value={`${p.fatherName ?? ''} (${p.fatherNin})`} mono /> : null}
        <Detail label="Informant" value={`${p.informantName} — ${p.informantRelation}`} />
        {p.informantContact ? <Detail label="Informant contact" value={p.informantContact} /> : null}
      </>
    );
  }
  if (e.eventType === 'DEATH') {
    const p = e.principals as DeathPrincipals;
    return (
      <>
        <Detail label="Deceased NIN" value={p.deceasedNin} mono />
        <Detail label="Informant" value={`${p.informantName} — ${p.informantRelation}`} />
        {p.informantContact ? <Detail label="Informant contact" value={p.informantContact} /> : null}
      </>
    );
  }
  const p = e.principals as MarriagePrincipals;
  return (
    <>
      <Detail label="Party A" value={`${p.partyAName} (${p.partyANin})`} mono />
      <Detail label="Party B" value={`${p.partyBName} (${p.partyBNin})`} mono />
      <Detail label="Matrimonial regime" value={p.regime.replaceAll('_', ' ')} />
      {p.place ? <Detail label="Place" value={p.place} /> : null}
    </>
  );
}

function EventDetailModal({
  id,
  missionByw,
  isStaff,
  isMissionHead,
  onClose,
  onChanged,
}: {
  id: string;
  missionByw: Map<string, DiplomaticMission>;
  isStaff: boolean;
  isMissionHead: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detailQ = useApiQuery<OverseasCivilEvent>(`/api/v1/m42/overseas-civil-events/${id}`);
  const e = detailQ.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? `${err.message} (${err.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={e ? `${e.eventType} abroad — ${principalHeadline(e)}` : 'Overseas civil event'} onClose={onClose} wide>
      {detailQ.error ? <ErrorNote message={detailQ.error} /> : null}
      {detailQ.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {e ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="State" value={<StatusBadge status={e.state} />} />
            <Detail label="Mission" value={missionByw.get(e.missionId)?.name ?? e.missionId} />
            <Detail label="Event date" value={formatDate(e.eventDate)} />
            {e.reportedContact ? <Detail label="Reporter contact" value={e.reportedContact} /> : null}
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-3 border-t border-slate-100 pt-4 text-sm sm:grid-cols-3">
            <PrincipalDetails e={e} />
          </div>

          {e.attestedBy ? <Detail label="Attested by" value={`${e.attestedBy} on ${formatDateTime(e.attestedAt)}`} /> : null}
          {e.m01RegistrationRef ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800">
              Forwarded to M01 — registration reference <span className="font-mono">{e.m01RegistrationRef}</span> on {formatDateTime(e.forwardedAt)}
            </div>
          ) : null}
          {e.state === 'REJECTED' && e.rejectionReason ? <ErrorNote message={`Rejected: ${e.rejectionReason}`} /> : null}

          {isStaff ? (
            <div className="space-y-3 border-t border-slate-100 pt-4">
              <div className="flex flex-wrap gap-2">
                {e.state === 'REPORTED' && isMissionHead ? (
                  <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m42/overseas-civil-events/${e.id}/attest`))}>
                    {busy ? 'Attesting…' : 'Attest (dual control)'}
                  </button>
                ) : null}
                {e.state === 'REPORTED' && !isMissionHead ? (
                  <p className="text-xs text-slate-500">Only the mission head can attest — and never the same person who reported it.</p>
                ) : null}
                {e.state === 'MISSION_ATTESTED' ? (
                  <button className="btn-success" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m42/overseas-civil-events/${e.id}/forward`))}>
                    {busy ? 'Forwarding…' : 'Forward to M01'}
                  </button>
                ) : null}
                {e.state === 'REPORTED' || e.state === 'MISSION_ATTESTED' ? (
                  <button className="btn-danger" disabled={busy} onClick={() => setShowReject((s) => !s)}>
                    Reject
                  </button>
                ) : null}
              </div>
              {showReject ? (
                <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50/50 p-3">
                  <Field label="Rejection reason">
                    <textarea className="field-input" rows={2} value={reason} onChange={(e2) => setReason(e2.target.value)} />
                  </Field>
                  <button
                    className="btn-danger"
                    disabled={busy || reason.trim().length < 3}
                    onClick={() => act(() => api.post(`/api/v1/m42/overseas-civil-events/${e.id}/reject`, { reason }))}
                  >
                    {busy ? 'Submitting…' : 'Confirm reject'}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <RoleNotice roles={['CONSULAR_OFFICER', 'MISSION_HEAD']} />
          )}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Reported {formatDateTime(e.createdAt)} · last updated {formatDateTime(e.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function CreateEventModal({ missions, onClose, onDone }: { missions: DiplomaticMission[]; onClose: () => void; onDone: () => void }) {
  const [missionCode, setMissionCode] = useState(missions[0]?.code ?? '');
  const [eventType, setEventType] = useState<OverseasEventType>('BIRTH');
  const [eventDate, setEventDate] = useState('');
  const [reportedContact, setReportedContact] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // BIRTH
  const [motherNin, setMotherNin] = useState('');
  const [motherName, setMotherName] = useState('');
  const [fatherNin, setFatherNin] = useState('');
  const [fatherName, setFatherName] = useState('');
  const [childGivenNames, setChildGivenNames] = useState('');
  const [childFamilyName, setChildFamilyName] = useState('');
  const [childSex, setChildSex] = useState<'MALE' | 'FEMALE'>('MALE');
  const [childPlaceOfBirth, setChildPlaceOfBirth] = useState('');
  const [informantName, setInformantName] = useState('');
  const [informantRelation, setInformantRelation] = useState<'MOTHER' | 'FATHER' | 'OTHER'>('MOTHER');
  const [informantContact, setInformantContact] = useState('');

  // DEATH
  const [deceasedNin, setDeceasedNin] = useState('');
  const [deathInformantName, setDeathInformantName] = useState('');
  const [deathInformantRelation, setDeathInformantRelation] = useState('');

  // MARRIAGE
  const [partyANin, setPartyANin] = useState('');
  const [partyAName, setPartyAName] = useState('');
  const [partyBNin, setPartyBNin] = useState('');
  const [partyBName, setPartyBName] = useState('');
  const [regime, setRegime] = useState<'COMMUNITY_OF_PROPERTY' | 'SEPARATION_OF_PROPERTY'>('SEPARATION_OF_PROPERTY');
  const [place, setPlace] = useState('');

  const ninOk = (v: string) => NIN_PATTERN.test(v.trim());

  const valid = useMemo(() => {
    if (!missionCode || !eventDate) return false;
    if (eventType === 'BIRTH') {
      return ninOk(motherNin) && motherName.trim().length >= 2 && childGivenNames.trim().length >= 1 && childFamilyName.trim().length >= 1 && childPlaceOfBirth.trim().length >= 2 && informantName.trim().length >= 2;
    }
    if (eventType === 'DEATH') {
      return ninOk(deceasedNin) && deathInformantName.trim().length >= 2 && deathInformantRelation.trim().length >= 2;
    }
    return ninOk(partyANin) && ninOk(partyBNin) && partyAName.trim().length >= 2 && partyBName.trim().length >= 2;
  }, [missionCode, eventDate, eventType, motherNin, motherName, childGivenNames, childFamilyName, childPlaceOfBirth, informantName, deceasedNin, deathInformantName, deathInformantRelation, partyANin, partyBNin, partyAName, partyBName]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      let principals: Record<string, unknown>;
      if (eventType === 'BIRTH') {
        principals = {
          motherNin: motherNin.trim(),
          motherName,
          fatherNin: fatherNin.trim() || undefined,
          fatherName: fatherName.trim() || undefined,
          childGivenNames,
          childFamilyName,
          childSex,
          childPlaceOfBirth,
          informantName,
          informantRelation,
          informantContact: informantContact.trim() || undefined,
        };
      } else if (eventType === 'DEATH') {
        principals = {
          deceasedNin: deceasedNin.trim(),
          informantName: deathInformantName,
          informantRelation: deathInformantRelation,
        };
      } else {
        principals = {
          partyANin: partyANin.trim(),
          partyAName,
          partyBNin: partyBNin.trim(),
          partyBName,
          regime,
          place: place.trim() || undefined,
        };
      }
      await api.post('/api/v1/m42/overseas-civil-events', {
        missionCode,
        eventType,
        eventDate,
        principals,
        reportedContact: reportedContact.trim() || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to report event');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Report an overseas civil event" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Mission">
            <select className="field-input" value={missionCode} onChange={(e) => setMissionCode(e.target.value)}>
              {missions.map((m) => (
                <option key={m.id} value={m.code}>
                  {m.name} ({m.code})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Event type">
            <select className="field-input" value={eventType} onChange={(e) => setEventType(e.target.value as OverseasEventType)}>
              {EVENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.charAt(0) + t.slice(1).toLowerCase()}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Event date">
            <input type="date" className="field-input" value={eventDate} onChange={(e) => setEventDate(e.target.value)} />
          </Field>
          <Field label="Reporter contact (optional)">
            <input className="field-input" value={reportedContact} onChange={(e) => setReportedContact(e.target.value)} placeholder="How to reach the family abroad" />
          </Field>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
          <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">{eventType} details</div>
          {eventType === 'BIRTH' ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Mother NIN">
                  <input className="field-input font-mono" value={motherNin} onChange={(e) => setMotherNin(e.target.value)} placeholder="NNN-NNNN-N-N-NN" />
                </Field>
                <Field label="Mother name">
                  <input className="field-input" value={motherName} onChange={(e) => setMotherName(e.target.value)} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Father NIN (optional)">
                  <input className="field-input font-mono" value={fatherNin} onChange={(e) => setFatherNin(e.target.value)} placeholder="NNN-NNNN-N-N-NN" />
                </Field>
                <Field label="Father name (optional)">
                  <input className="field-input" value={fatherName} onChange={(e) => setFatherName(e.target.value)} />
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Child given names">
                  <input className="field-input" value={childGivenNames} onChange={(e) => setChildGivenNames(e.target.value)} />
                </Field>
                <Field label="Child family name">
                  <input className="field-input" value={childFamilyName} onChange={(e) => setChildFamilyName(e.target.value)} />
                </Field>
                <Field label="Sex">
                  <select className="field-input" value={childSex} onChange={(e) => setChildSex(e.target.value as 'MALE' | 'FEMALE')}>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                  </select>
                </Field>
              </div>
              <Field label="Place of birth">
                <input className="field-input" value={childPlaceOfBirth} onChange={(e) => setChildPlaceOfBirth(e.target.value)} placeholder="e.g. Queen Charlotte's Hospital, London" />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Informant name">
                  <input className="field-input" value={informantName} onChange={(e) => setInformantName(e.target.value)} />
                </Field>
                <Field label="Informant relation">
                  <select className="field-input" value={informantRelation} onChange={(e) => setInformantRelation(e.target.value as typeof informantRelation)}>
                    <option value="MOTHER">Mother</option>
                    <option value="FATHER">Father</option>
                    <option value="OTHER">Other</option>
                  </select>
                </Field>
                <Field label="Informant contact (optional)">
                  <input className="field-input" value={informantContact} onChange={(e) => setInformantContact(e.target.value)} />
                </Field>
              </div>
            </div>
          ) : eventType === 'DEATH' ? (
            <div className="space-y-3">
              <Field label="Deceased NIN">
                <input className="field-input font-mono" value={deceasedNin} onChange={(e) => setDeceasedNin(e.target.value)} placeholder="NNN-NNNN-N-N-NN" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Informant name">
                  <input className="field-input" value={deathInformantName} onChange={(e) => setDeathInformantName(e.target.value)} />
                </Field>
                <Field label="Informant relation">
                  <input className="field-input" value={deathInformantRelation} onChange={(e) => setDeathInformantRelation(e.target.value)} placeholder="e.g. Son" />
                </Field>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Party A NIN">
                  <input className="field-input font-mono" value={partyANin} onChange={(e) => setPartyANin(e.target.value)} placeholder="NNN-NNNN-N-N-NN" />
                </Field>
                <Field label="Party A name">
                  <input className="field-input" value={partyAName} onChange={(e) => setPartyAName(e.target.value)} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Party B NIN">
                  <input className="field-input font-mono" value={partyBNin} onChange={(e) => setPartyBNin(e.target.value)} placeholder="NNN-NNNN-N-N-NN" />
                </Field>
                <Field label="Party B name">
                  <input className="field-input" value={partyBName} onChange={(e) => setPartyBName(e.target.value)} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Matrimonial regime">
                  <select className="field-input" value={regime} onChange={(e) => setRegime(e.target.value as typeof regime)}>
                    <option value="SEPARATION_OF_PROPERTY">Separation of property</option>
                    <option value="COMMUNITY_OF_PROPERTY">Community of property</option>
                  </select>
                </Field>
                <Field label="Place (optional)">
                  <input className="field-input" value={place} onChange={(e) => setPlace(e.target.value)} />
                </Field>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !valid}>
            {busy ? 'Reporting…' : 'Report event'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
